import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { 
  Link as LinkIcon, CheckCircle2, XCircle, Loader2, 
  ExternalLink, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Provider logos
const PROVIDER_LOGOS = {
  sharepoint: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#0078D4"/>
      <path d="M8 8h8v8H8V8z" fill="white"/>
      <path d="M9 9h2v2H9V9zm4 0h2v2h-2V9zm-4 4h2v2H9v-2zm4 0h2v2h-2v-2z" fill="#0078D4"/>
    </svg>
  ),
  googledrive: (
    <svg className="w-8 h-8" viewBox="0 0 24 24">
      <path d="M8.627 21h6.746L12 14.5 5.254 21h3.373z" fill="#3777E3"/>
      <path d="M12 14.5L18.746 21h3.373L14.373 7.5H9.627L12 14.5z" fill="#FFCF63"/>
      <path d="M1.881 14.5L5.254 21l6.746-6.5L8.627 7.5l-6.746 7z" fill="#11A861"/>
    </svg>
  )
};

export default function IntegrationSettings() {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    loadServices();
  }, [token]);

  const loadServices = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/documents/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data || []);
    } catch (err) {
      console.error('Failed to load services:', err);
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    setConnecting(provider);
    
    try {
      // Get auth URL from backend
      const redirectUri = `${window.location.origin}/app/settings?tab=integrations&callback=${provider}`;
      const response = await axios.get(`${API_BASE}/documents/services/${provider}/auth-url`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { redirect_uri: redirectUri }
      });
      
      // Store provider in session for callback
      sessionStorage.setItem('oauth_provider', provider);
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      
      // Redirect to OAuth
      window.location.href = response.data.authUrl;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start connection');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!confirm(`Are you sure you want to disconnect ${provider === 'sharepoint' ? 'SharePoint/OneDrive' : 'Google Drive'}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE}/documents/services/${provider}/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service disconnected');
      loadServices();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const callback = urlParams.get('callback');
    const storedProvider = sessionStorage.getItem('oauth_provider');
    const storedRedirectUri = sessionStorage.getItem('oauth_redirect_uri');
    
    if (code && (callback || storedProvider)) {
      const provider = callback || storedProvider;
      
      // Exchange code for tokens
      const exchangeToken = async () => {
        setConnecting(provider);
        try {
          await axios.post(`${API_BASE}/documents/services/connect`, {
            provider,
            authCode: code,
            redirectUri: storedRedirectUri
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`${provider === 'sharepoint' ? 'SharePoint/OneDrive' : 'Google Drive'} connected successfully!`);
          
          // Clear URL params and session storage
          sessionStorage.removeItem('oauth_provider');
          sessionStorage.removeItem('oauth_redirect_uri');
          window.history.replaceState({}, '', `${window.location.pathname}?tab=integrations`);
          
          loadServices();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to complete connection');
        } finally {
          setConnecting(null);
        }
      };
      
      exchangeToken();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[#2C2420] mb-1">Document Integrations</h3>
        <p className="text-sm text-[#5D524D]">
          Connect your cloud storage accounts to browse and link documents directly to clients.
        </p>
      </div>

      <div className="grid gap-4">
        {services.map((service) => (
          <div
            key={service.id}
            className="p-4 border border-[#E6E0D9] rounded-lg bg-white"
            data-testid={`integration-${service.provider}`}
          >
            <div className="flex items-start gap-4">
              {/* Logo */}
              <div className="flex-shrink-0">
                {PROVIDER_LOGOS[service.provider] || (
                  <div className="w-8 h-8 bg-[#4A3B32] rounded flex items-center justify-center">
                    <LinkIcon className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-[#2C2420]">{service.name}</h4>
                  {service.connected ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected
                    </span>
                  ) : !service.configured ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Not Configured
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-[#5D524D] bg-[#F5F3F0] px-2 py-0.5 rounded-full">
                      <XCircle className="w-3 h-3" />
                      Not Connected
                    </span>
                  )}
                </div>
                
                {service.connected && service.connectedAt && (
                  <p className="text-xs text-[#8C8078] mt-1">
                    Connected on {new Date(service.connectedAt).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                )}
                
                {!service.configured && (
                  <p className="text-xs text-amber-600 mt-1">
                    {service.provider === 'sharepoint' 
                      ? 'Requires MICROSOFT_CLIENT_ID, MICROSOFT_CLIENT_SECRET, and MICROSOFT_TENANT_ID environment variables.'
                      : 'Requires GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET environment variables.'}
                  </p>
                )}
                
                <p className="text-sm text-[#5D524D] mt-2">
                  {service.provider === 'sharepoint'
                    ? 'Browse and link documents from SharePoint sites and OneDrive for Business.'
                    : 'Browse and link documents from your Google Drive account.'}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex-shrink-0">
                {service.connected ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadServices()}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(service.provider)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleConnect(service.provider)}
                    disabled={connecting === service.provider || !service.configured}
                    className="bg-[#4A3B32] hover:bg-[#3A2B22]"
                  >
                    {connecting === service.provider ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Connect
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Setup Instructions */}
      <div className="mt-8 p-4 bg-[#F9F7F5] rounded-lg border border-[#E6E0D9]">
        <h4 className="font-medium text-[#2C2420] mb-2">Setup Instructions</h4>
        <div className="text-sm text-[#5D524D] space-y-3">
          <div>
            <strong className="text-[#2C2420]">SharePoint/OneDrive:</strong>
            <ol className="list-decimal list-inside mt-1 space-y-1">
              <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-[#4A3B32] underline">Azure Portal App Registrations</a></li>
              <li>Create a new application registration</li>
              <li>Add redirect URI: <code className="bg-white px-1 py-0.5 rounded text-xs">{window.location.origin}/app/settings</code></li>
              <li>Grant permissions: Files.ReadWrite.All, Sites.Read.All</li>
              <li>Create a client secret and copy the values</li>
              <li>Add MICROSOFT_CLIENT_ID, MICROSOFT_CLIENT_SECRET, MICROSOFT_TENANT_ID to environment</li>
            </ol>
          </div>
          <div>
            <strong className="text-[#2C2420]">Google Drive:</strong>
            <ol className="list-decimal list-inside mt-1 space-y-1">
              <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#4A3B32] underline">Google Cloud Console</a></li>
              <li>Create OAuth 2.0 credentials</li>
              <li>Add authorized redirect URI: <code className="bg-white px-1 py-0.5 rounded text-xs">{window.location.origin}/app/settings</code></li>
              <li>Enable Google Drive API</li>
              <li>Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to environment</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
